/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from 'antd/lib/upload/UploadList/index.tsx';
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import CSSMotion, { CSSMotionList } from 'rc-motion';
import classNames from 'classnames';
import { cloneElement, isValidElement } from 'antd/lib/_util/reactNode';
import { previewImage, isImageUrl } from 'antd/lib/upload/utils';
// import collapseMotion from 'antd/lib/_util/motion';
import OpacityMotion from '../../_util/motion';
import { ConfigContext } from '../../config-provider';
import { Button } from '../../button';
import useForceUpdate from 'antd/lib/_util/hooks/useForceUpdate';
import ListItem from './ListItem';
import { Icon } from '../../icon';
const listItemMotion = {
    // ...collapseMotion
    ...OpacityMotion
};
delete listItemMotion.onAppearEnd;
delete listItemMotion.onEnterEnd;
delete listItemMotion.onLeaveEnd;
const InternalUploadList = ({ listType, className, previewFile, onPreview, onDownload, onRemove, locale, size, iconRender, isImageUrl: isImgUrl, prefixCls: customizePrefixCls, items = [], showPreviewIcon, showRemoveIcon, showDownloadIcon, removeIcon, downloadIcon, progress: customizeProgress, appendAction, itemRender, 
// theme,
type }, ref) => {
    const forceUpdate = useForceUpdate();
    const [motionAppear, setMotionAppear] = React.useState(false);
    // ============================= Effect =============================
    React.useEffect(() => {
        if (listType !== 'picture' && listType !== 'picture-card' && type !== 'simple') {
            return;
        }
        // @ts-ignore
        (items || []).forEach((file) => {
            if (typeof document === 'undefined' ||
                typeof window === 'undefined' ||
                !window.FileReader ||
                !window.File ||
                !(file.originFileObj instanceof File || file.originFileObj instanceof Blob) ||
                file.thumbUrl !== undefined) {
                return;
            }
            file.thumbUrl = '';
            if (previewFile) {
                previewFile(file.originFileObj).then((previewDataUrl) => {
                    // Need append '' to avoid dead loop
                    file.thumbUrl = previewDataUrl || '';
                    forceUpdate();
                });
            }
        });
    }, [listType, items, previewFile]);
    React.useEffect(() => {
        setMotionAppear(true);
    }, []);
    // ============================= Events =============================
    const onInternalPreview = (file, e) => {
        if (!onPreview) {
            return;
        }
        e?.preventDefault();
        return onPreview(file);
    };
    const onInternalDownload = (file) => {
        if (typeof onDownload === 'function') {
            onDownload(file);
        }
        else if (file.url) {
            window.open(file.url);
        }
    };
    const onInternalClose = (file) => {
        onRemove?.(file);
    };
    const internalIconRender = (file) => {
        if (iconRender) {
            return iconRender(file, listType);
        }
        const isLoading = file.status === 'uploading';
        const fileIcon = isImgUrl && isImgUrl(file) ? (file.status === 'error' ? (React.createElement(Icon, { type: 'no-image--outline', size: 'small' })) : (React.createElement(Icon, { type: 'image--outline', size: 'small' }))) : (React.createElement(Icon, { type: 'attachment', size: 'small' }));
        let icon = fileIcon;
        if (listType === 'picture-card') {
            icon = isLoading ? locale.uploading : fileIcon;
        }
        return icon;
    };
    const actionIconRender = (customIcon, callback, prefixCls, title, role) => {
        const btnProps = {
            type: 'text',
            size: 'small',
            title,
            onClick: (e) => {
                callback();
                if (isValidElement(customIcon) && customIcon.props.onClick) {
                    customIcon.props.onClick(e);
                }
            },
            className: classNames(`${prefixCls}-list-item-card-actions-btn`, {
                [`${prefixCls}-list-item-card-actions-btn-${role}`]: role !== undefined
            })
        };
        if (isValidElement(customIcon)) {
            const btnIcon = cloneElement(customIcon, {
                ...customIcon.props,
                onClick: () => { }
            });
            return React.createElement(Button, { ...btnProps, icon: btnIcon });
        }
        return (React.createElement(Button, { ...btnProps },
            React.createElement("span", null, customIcon)));
    };
    // ============================== Ref ===============================
    // Test needs
    React.useImperativeHandle(ref, () => ({
        handlePreview: onInternalPreview,
        handleDownload: onInternalDownload
    }));
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    // ============================= Render =============================
    const prefixCls = getPrefixCls('upload', customizePrefixCls);
    const listClassNames = classNames({
        [`${prefixCls}-list`]: true,
        [`${prefixCls}-list-${listType}`]: true,
        [`${prefixCls}-list-rtl`]: direction === 'rtl',
        // [`${prefixCls}-list-gray`]: theme === 'gray',
        [`${prefixCls}-list-${size}`]: size
    }, className);
    // >>> Motion config
    const motionKeyList = [
        ...items.map((file) => ({
            key: file.uid,
            file
        }))
    ];
    const animationDirection = listType === 'picture-card' || type === 'simple' ? 'animate-inline' : 'animate';
    // const transitionName = list.length === 0 ? '' : `${prefixCls}-${animationDirection}`;
    let motionConfig = {
        motionDeadline: 2000,
        motionName: `${prefixCls}-${animationDirection}`,
        keys: motionKeyList,
        motionAppear
    };
    if (listType !== 'picture-card') {
        motionConfig = {
            ...listItemMotion,
            ...motionConfig
        };
    }
    if (type === 'simple') {
        motionConfig = {
            ...listItemMotion,
            ...motionConfig,
            motionAppear: false
        };
    }
    let progress = customizeProgress;
    if (typeof customizeProgress === 'undefined') {
        if (listType === 'picture-card' || (type === 'simple' && listType === 'text')) {
            progress = {
                strokeWidth: 2,
                showInfo: false
            };
        }
        else if (listType === 'text' || listType === 'picture') {
            let width = 32;
            if (size === 'small') {
                width = 28;
            }
            else if (size === 'large') {
                width = 40;
            }
            progress = {
                strokeWidth: width,
                showInfo: false,
                style: {
                    backgroundImage: 'initial'
                }
            };
        }
    }
    return (React.createElement("div", { className: listClassNames },
        React.createElement(CSSMotionList, { ...motionConfig, component: false }, ({ key, file, className: motionClassName, style: motionStyle }) => (React.createElement(ListItem, { key: key, locale: locale, prefixCls: prefixCls, 
            // className={type === 'simple' ? undefined : motionClassName}
            // style={type === 'simple' ? undefined : motionStyle}
            className: motionClassName, style: motionStyle, file: file, items: items, progress: progress, listType: listType, isImgUrl: isImgUrl, showPreviewIcon: showPreviewIcon, showRemoveIcon: showRemoveIcon, showDownloadIcon: showDownloadIcon, removeIcon: removeIcon, downloadIcon: downloadIcon, iconRender: internalIconRender, actionIconRender: actionIconRender, itemRender: itemRender, onPreview: onInternalPreview, onDownload: onInternalDownload, onClose: onInternalClose, type: type }))),
        appendAction && (React.createElement(CSSMotion, { ...motionConfig }, ({ className: motionClassName, style: motionStyle }) => cloneElement(appendAction, (oriProps) => {
            return type === 'simple'
                ? {}
                : {
                    className: classNames(oriProps.className, motionClassName),
                    style: {
                        ...motionStyle,
                        ...oriProps.style
                    }
                };
        })))));
};
const UploadList = React.forwardRef(InternalUploadList);
UploadList.displayName = 'UploadList';
UploadList.defaultProps = {
    listType: 'text',
    showRemoveIcon: true,
    showDownloadIcon: true,
    showPreviewIcon: true,
    previewFile: previewImage,
    isImageUrl
};
export default UploadList;
